import { useState, type CSSProperties } from 'react';
import { EquityOrbit } from '../../components/EquityOrbit';
import { MilestoneCard } from '../../components/MilestoneCard';
import { ScenarioControls } from '../../components/ScenarioControls';
import { SourceDrawer } from '../../components/SourceDrawer';
import { SummaryPanel } from '../../components/SummaryPanel';
import { Timeline } from '../../components/Timeline';
import { formatBps } from '../../domain/equityEngine';
import { ADVERSE_EVENT_LABELS, STATUS_LABELS } from '../../domain/legalAssumptions';
import type {
  DemoContract,
  MilestoneCalculation,
  Scenario,
  ScenarioCalculation,
} from '../../domain/types';

interface EquityJourneyModeProps {
  contract: DemoContract;
  calculation: ScenarioCalculation;
  selectedCalculation: MilestoneCalculation;
  selectedMilestoneId: string;
  onSelectMilestone: (milestoneId: string) => void;
  onScenarioChange: (updater: (scenario: Scenario) => Scenario) => void;
  onReset: () => void;
  onRandom: () => void;
  onExercise: (milestoneId: string) => void;
}

type MobileModule = 'scenario' | 'overview' | 'milestones' | 'evidence';
type ExperienceMode = 'guided' | 'panel';

const MOBILE_MODULES: Array<{ id: MobileModule; label: string }> = [
  { id: 'scenario', label: '推演' },
  { id: 'overview', label: '总览' },
  { id: 'milestones', label: '里程碑' },
  { id: 'evidence', label: '条款' },
];

export function EquityJourneyMode({
  contract,
  calculation,
  selectedCalculation,
  selectedMilestoneId,
  onSelectMilestone,
  onScenarioChange,
  onReset,
  onRandom,
  onExercise,
}: EquityJourneyModeProps) {
  const [activeMobileModule, setActiveMobileModule] = useState<MobileModule>('scenario');
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>('guided');
  const selectedIndex = Math.max(
    0,
    calculation.milestones.findIndex(
      (milestoneCalculation) => milestoneCalculation.milestone.id === selectedMilestoneId,
    ),
  );
  const selectedMilestone = selectedCalculation.milestone;
  const sourceClause = selectedMilestone.sourceClause;
  const selectedStatusLabel = STATUS_LABELS[selectedCalculation.status];
  const totalPotentialBps = Math.max(1, calculation.summary.potentialBps);
  const metricItems = [
    {
      label: '最高孵化权益',
      value: formatBps(calculation.summary.potentialBps),
      progress: 100,
    },
    {
      label: '里程碑成熟',
      value: formatBps(calculation.summary.milestoneEarnedBps),
      progress: toProgress(calculation.summary.milestoneEarnedBps, totalPotentialBps),
    },
    {
      label: '时间归属',
      value: formatBps(calculation.summary.timeVestedBps),
      progress: toProgress(calculation.summary.timeVestedBps, totalPotentialBps),
    },
    {
      label: '当前可行权',
      value: formatBps(calculation.summary.exercisableBps),
      progress: toProgress(calculation.summary.exercisableBps, totalPotentialBps),
    },
  ];

  return (
    <section className="console-grid" aria-label="孵化权益推演">
      <div className="paper-statusbar" aria-label="当前推演状态">
        <span>
          第 {selectedIndex + 1} / {calculation.milestones.length} 项里程碑
        </span>
        <span>{calculation.scenario.asOfDate}</span>
        <span>{selectedStatusLabel}</span>
        <span>目标公司</span>
        <span>{ADVERSE_EVENT_LABELS[calculation.scenario.adverseEventReason]}</span>
      </div>

      <nav className="experience-switch" aria-label="推演模式切换">
        <button
          className={experienceMode === 'guided' ? 'is-active' : ''}
          type="button"
          aria-pressed={experienceMode === 'guided'}
          onClick={() => setExperienceMode('guided')}
        >
          快速推演
        </button>
        <button
          className={experienceMode === 'panel' ? 'is-active' : ''}
          type="button"
          aria-pressed={experienceMode === 'panel'}
          onClick={() => setExperienceMode('panel')}
        >
          完整面板
        </button>
      </nav>

      <nav className="mobile-module-tabs" aria-label="移动端模块切换">
        {MOBILE_MODULES.map((module) => (
          <button
            className={activeMobileModule === module.id ? 'is-active' : ''}
            type="button"
            key={module.id}
            onClick={() => setActiveMobileModule(module.id)}
          >
            {module.label}
          </button>
        ))}
      </nav>

      <section
        className={`control-panels mobile-module ${
          activeMobileModule === 'scenario' ? 'is-active' : ''
        } ${experienceMode === 'guided' ? 'is-guided-view' : 'is-panel-view'}`}
        aria-label="推演控制与汇总"
      >
        <ScenarioControls
          calculation={calculation}
          selectedCalculation={selectedCalculation}
          mode={experienceMode === 'guided' ? 'guided' : 'panel'}
          onRequestPanel={() => setExperienceMode('panel')}
          onChange={onScenarioChange}
          onReset={onReset}
          onRandom={onRandom}
          onExercise={onExercise}
        />
        <SummaryPanel summary={calculation.summary} />
        <Timeline calculation={selectedCalculation} scenario={calculation.scenario} />
      </section>

      <section
        className={`metric-strip mobile-module ${
          activeMobileModule === 'overview' ? 'is-active' : ''
        }`}
        aria-label="权益指标概览"
      >
        {metricItems.map((metric) => (
          <article className="metric-ticket" key={metric.label}>
            <div>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </div>
            <i
              aria-hidden="true"
              style={{ inlineSize: `${metric.progress}%` } as CSSProperties}
            />
          </article>
        ))}
      </section>

      <section
        className={`narrative-stage mobile-module ${
          activeMobileModule === 'overview' ? 'is-active' : ''
        }`}
        aria-label="叙事推演"
      >
        <div className="stage-copy">
          <span className="eyebrow">协议推演 / 附条件权益</span>
          <h2>
            孵化权益
            <br />
            推演
          </h2>
          <p>{contract.sourceNotice}</p>
        </div>

        <div className="stage-reading">
          <span className="eyebrow">
            {selectedMilestone.id} / 条款 {sourceClause}
          </span>
          <h3>{selectedMilestone.title}</h3>
          <p>{selectedMilestone.description}</p>
          {selectedMilestone.condition ? (
            <p className="condition-note">{selectedMilestone.condition}</p>
          ) : null}
          <dl className="reading-facts">
            <div>
              <dt>当前状态</dt>
              <dd>{selectedStatusLabel}</dd>
            </div>
            <div>
              <dt>成熟权益</dt>
              <dd>{formatBps(selectedCalculation.milestoneEarnedBps)}</dd>
            </div>
            <div>
              <dt>当前可行权</dt>
              <dd>{formatBps(selectedCalculation.exercisableBps)}</dd>
            </div>
          </dl>
        </div>

        <EquityOrbit
          summary={calculation.summary}
          milestones={calculation.milestones}
          selectedMilestoneId={selectedMilestoneId}
          onSelectMilestone={onSelectMilestone}
        />
      </section>

      <section
        className={`milestone-zone mobile-module ${
          activeMobileModule === 'milestones' ? 'is-active' : ''
        }`}
        aria-label="六项里程碑"
      >
        <div className="section-heading">
          <span className="eyebrow">六项里程碑</span>
          <h2>逐项查看条件、状态与条款依据</h2>
        </div>
        <div className="milestone-grid">
          {calculation.milestones.map((milestoneCalculation) => (
            <MilestoneCard
              key={milestoneCalculation.milestone.id}
              calculation={milestoneCalculation}
              selected={milestoneCalculation.milestone.id === selectedMilestoneId}
              onSelect={() => onSelectMilestone(milestoneCalculation.milestone.id)}
            />
          ))}
        </div>
      </section>

      <section
        className={`evidence-panels mobile-module ${
          activeMobileModule === 'evidence' ? 'is-active' : ''
        }`}
        aria-label="条款来源"
      >
        <SourceDrawer
          calculation={selectedCalculation}
          scenario={calculation.scenario}
          assumptions={calculation.assumptions}
        />
      </section>
    </section>
  );
}

function toProgress(value: number, total: number): number {
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}
